/**
 * Build-time configuration for the frontend.
 * API endpoints are fixed at build time via NEXT_PUBLIC_* env vars.
 */

import { AppConfig, BackendConfigResponse } from '@/lib/types/config'
import { runtimeTarget } from '@/lib/runtime-target'
import { fetchWithAuth } from '@/lib/utils/request'

// Build timestamp for debugging - set at build time
const BUILD_TIME = new Date().toISOString()

let config: AppConfig | null = null
let configPromise: Promise<AppConfig> | null = null

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

/**
 * Get the API URL to use for requests.
 *
 * Priority:
 * 1. Environment variable (NEXT_PUBLIC_API_URL)
 * 2. Default fallback (http://localhost:5055)
 */
export async function getApiUrl(): Promise<string> {
  // If we already have config, return it
  if (config) {
    return config.apiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.apiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.apiUrl
}

/**
 * Get the Matrix API URL to use for requests.
 */
export async function getMatrixApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.matrixApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.matrixApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.matrixApiUrl
}

/**
 * Get the Cuss API URL to use for requests.
 */
export async function getCussApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.cussApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.cussApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.cussApiUrl
}

/**
 * Get the GG API URL to use for requests.
 */
export async function getGgApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.ggApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.ggApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.ggApiUrl
}

/**
 * Get the Athena API URL to use for requests.
 */
export async function getAthenaApiUrl(): Promise<string | undefined> {
  // If we already have config, return it
  if (config) {
    return config.athenaApiUrl
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.athenaApiUrl
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.athenaApiUrl
}

/**
 * Get the API URL base for /auth/*, /mainD/*, /fs/* proxy calls.
 *
 * Priority:
 * 1. Environment variable (NEXT_PUBLIC_AUTH_API_URL)
 * 2. Auto-computed proxy: {apiUrl}/agent-api
 *
 * Callers append their full path, e.g.:
 *   getAuthApiUrl() + '/auth/customer/login/check'
 *   getAuthApiUrl() + '/mainD/material/materialInfo'
 *   getAuthApiUrl() + '/fs/materialDownloadForDashboard/getFileUrl'
 */
export async function getAuthApiUrl(): Promise<string> {
  // If we already have config, return it
  if (config) {
    return config.authApiUrl || `${config.apiUrl}/agent-api`
  }

  // If we're already fetching, wait for that
  if (configPromise) {
    const cfg = await configPromise
    return cfg.authApiUrl || `${cfg.apiUrl}/agent-api`
  }

  // Start fetching config
  configPromise = fetchConfig()
  const cfg = await configPromise
  return cfg.authApiUrl || `${cfg.apiUrl}/agent-api`
}

/**
 * Get the full configuration.
 */
export async function getConfig(): Promise<AppConfig> {
  if (config) {
    return config
  }

  if (configPromise) {
    return await configPromise
  }

  configPromise = fetchConfig()
  return await configPromise
}

/**
 * Fetch configuration from the API or use defaults.
 */
async function fetchConfig(): Promise<AppConfig> {
  console.log('🔧 [Config] Starting configuration detection...')
  console.log('🔧 [Config] Build time:', BUILD_TIME)

  // STEP 1: Build-time environment variables
  const envApiUrl = process.env.NEXT_PUBLIC_API_URL
  const envDesktopApiUrl = process.env.NEXT_PUBLIC_DESKTOP_API_URL
  const envMatrixApiUrl = process.env.NEXT_PUBLIC_MATRIX_API_URL
  const envCussApiUrl = process.env.NEXT_PUBLIC_CUSS_API_URL
  const envGgApiUrl = process.env.NEXT_PUBLIC_GG_API_URL
  const envAthenaApiUrl = process.env.NEXT_PUBLIC_ATHENA_API_URL
  const envDashboardApiUrl = process.env.NEXT_PUBLIC_DASHBOARD_API_URL
  const envAuthApiUrl = process.env.NEXT_PUBLIC_AUTH_API_URL
  console.log('🔧 [Config] NEXT_PUBLIC_API_URL from build:', envApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_DESKTOP_API_URL from build:', envDesktopApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_MATRIX_API_URL from build:', envMatrixApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_CUSS_API_URL from build:', envCussApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_GG_API_URL from build:', envGgApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_ATHENA_API_URL from build:', envAthenaApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_DASHBOARD_API_URL from build:', envDashboardApiUrl || '(not set)')
  console.log('🔧 [Config] NEXT_PUBLIC_AUTH_API_URL from build:', envAuthApiUrl || '(not set)')

  // STEP 2: Runtime defaults. Desktop startup is owned by the Tauri shell,
  // so the frontend avoids Tauri IPC during boot and waits on the local HTTP API.
  // desktop runtime can explicitly set NEXT_PUBLIC_DESKTOP_API_URL
  let defaultApiUrl = runtimeTarget === 'desktop'
    ? (envDesktopApiUrl || 'http://127.0.0.1:5055')
    : 'http://localhost:5055'

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    const protocol = window.location.protocol
    console.log('🔧 [Config] Current frontend URL:', `${protocol}//${hostname}${window.location.port ? ':' + window.location.port : ''}`)

    // If not localhost, use the same hostname with port 5055
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      defaultApiUrl = `${protocol}//${hostname}:5055`
      console.log('🔧 [Config] Detected remote hostname, using:', defaultApiUrl)
    } else {
      console.log('🔧 [Config] Detected localhost, using:', defaultApiUrl)
    }
  }

  // Priority:
  // web: NEXT_PUBLIC_API_URL > smart default
  // desktop: NEXT_PUBLIC_DESKTOP_API_URL > NEXT_PUBLIC_API_URL > desktop default
  const baseUrl = runtimeTarget === 'desktop'
    ? (envDesktopApiUrl || envApiUrl || defaultApiUrl)
    : (envApiUrl || defaultApiUrl)
  console.log('🔧 [Config] Final base URL to try:', baseUrl)
  console.log('🔧 [Config] Selection priority: build-time=' + (envApiUrl ? '✅' : '❌') +
    ', smart-default=' + (!envApiUrl ? '✅' : '❌'))

  try {
    console.log('🔧 [Config] Fetching backend config from:', `${baseUrl}/agent-api/config`)
    const data = await fetchBackendConfig(baseUrl)
    config = {
      apiUrl: baseUrl, // Use baseUrl from runtime-config (Python no longer returns this)
      matrixApiUrl: envMatrixApiUrl || undefined,
      cussApiUrl: envCussApiUrl || undefined,
      ggApiUrl: envGgApiUrl || undefined,
      athenaApiUrl: envAthenaApiUrl || undefined,
      dashboardApiUrl: envDashboardApiUrl || undefined,
      authApiUrl: envAuthApiUrl || undefined,
      version: data.version || 'unknown',
      buildTime: BUILD_TIME,
      latestVersion: data.latestVersion || null,
      hasUpdate: data.hasUpdate || false,
      dbStatus: data.dbStatus, // Can be undefined for old backends
      deployMode: data.deployMode,
      // VOD 上传开关：默认 true（保持现有行为），后端返回 undefined 时回退到 true
      useVodInMaterialLibrary: data.useVodInMaterialLibrary ?? true,
      useVodInProgram: data.useVodInProgram ?? true,
    }
    console.log('✅ [Config] Successfully loaded API config:', config)
    return config
  } catch (error) {
    // Don't log error here - ConnectionGuard will display it with proper UI
    throw error
  }
}

async function fetchBackendConfig(baseUrl: string): Promise<BackendConfigResponse> {
  const attempts = runtimeTarget === 'desktop' ? 60 : 1
  let lastError: unknown = null
  let lastResponse: BackendConfigResponse | null = null

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const response = await fetchWithAuth(`${baseUrl}/agent-api/config`, {
        cache: 'no-store',
      })

      if (response.ok) {
        const data = await response.json() as BackendConfigResponse
        lastResponse = data
        if (data.dbStatus !== 'offline') {
          return data
        }
        lastError = new Error('Database is not ready')
      } else {
        lastError = new Error(`API config endpoint returned status ${response.status}`)
      }
    } catch (error) {
      lastError = error
    }

    if (attempt < attempts) {
      console.log(`🔧 [Config] Waiting for desktop API (${attempt}/${attempts})...`)
      await sleep(1000)
    }
  }

  if (lastResponse) {
    return lastResponse
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError))
}

/**
 * Reset the configuration cache (useful for testing).
 */
export function resetConfig(): void {
  config = null
  configPromise = null
}
